import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  HourglassEmpty as PendingIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface PendingUser {
  id: number;
  email: string;
  email_verified: boolean;
  account_status: string;
  created_at: string;
}

const PendingUsers: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState<PendingUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: number | null;
    action: 'approve' | 'reject' | 'delete' | null;
    email: string;
  }>({
    open: false,
    userId: null,
    action: null,
    email: ''
  });

  const fetchPendingUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await api.get('/auth/admin/pending-users');

      if (response.data.success) {
        setUsers(response.data.data.users);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de la récupération des comptes en attente';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const handleOpenConfirmDialog = (userId: number, action: 'approve' | 'reject' | 'delete', email: string) => {
    setConfirmDialog({
      open: true,
      userId,
      action,
      email
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      userId: null,
      action: null,
      email: ''
    });
  };

  const handleConfirmAction = async () => {
    const { userId, action } = confirmDialog;
    if (!userId || !action) return;

    try {
      let endpoint = '';
      let method = 'post';

      if (action === 'approve') {
        endpoint = 'approve-user';
      } else if (action === 'reject') {
        endpoint = 'reject-user';
      } else if (action === 'delete') {
        endpoint = 'users';
        method = 'delete';
      }

      let response;
      if (method === 'delete') {
        response = await api.delete(`/auth/admin/${endpoint}/${userId}`);
      } else {
        response = await api.post(`/auth/admin/${endpoint}/${userId}`, {});
      }

      if (response.data.success) {
        setSuccess(
          action === 'approve'
            ? `Compte ${confirmDialog.email} validé avec succès`
            : action === 'reject'
            ? `Compte ${confirmDialog.email} rejeté`
            : `Compte ${confirmDialog.email} supprimé`
        );
        // Refresh the list
        fetchPendingUsers();
        handleCloseConfirmDialog();

        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || `Erreur lors de l'opération`;
      setError(errorMessage);
      handleCloseConfirmDialog();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Vous n'avez pas les droits d'accès à cette page
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Comptes en attente de validation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez les demandes d'inscription et validez les nouveaux utilisateurs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualiser">
            <IconButton
              onClick={fetchPendingUsers}
              disabled={isLoading}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/admin')}
          >
            Retour
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper elevation={3}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <PendingIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucun compte en attente
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tous les comptes ont été traités
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email vérifié</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Statut</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date d'inscription</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((pendingUser) => (
                  <TableRow
                    key={pendingUser.id}
                    sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {pendingUser.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={pendingUser.email_verified ? 'Vérifié' : 'Non vérifié'}
                        color={pendingUser.email_verified ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="En attente"
                        color="warning"
                        size="small"
                        icon={<PendingIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(pendingUser.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="Approuver">
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<ApproveIcon />}
                            onClick={() => handleOpenConfirmDialog(pendingUser.id, 'approve', pendingUser.email)}
                            disabled={!pendingUser.email_verified}
                          >
                            Valider
                          </Button>
                        </Tooltip>
                        <Tooltip title="Rejeter">
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<RejectIcon />}
                            onClick={() => handleOpenConfirmDialog(pendingUser.id, 'reject', pendingUser.email)}
                          >
                            Rejeter
                          </Button>
                        </Tooltip>
                        <Tooltip title="Supprimer définitivement">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleOpenConfirmDialog(pendingUser.id, 'delete', pendingUser.email)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {!pendingUser.email_verified && (
                        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                          L'email doit être vérifié avant validation
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Box sx={{ mt: 2, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
        <Typography variant="body2" color="info.dark">
          <strong>Note :</strong> Les utilisateurs doivent d'abord vérifier leur adresse email avant que vous puissiez approuver leur compte.
          Une fois approuvé, l'utilisateur recevra un email de confirmation et pourra se connecter.
        </Typography>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
      >
        <DialogTitle>
          {confirmDialog.action === 'approve'
            ? 'Confirmer la validation'
            : confirmDialog.action === 'reject'
            ? 'Confirmer le rejet'
            : 'Confirmer la suppression'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'approve' ? (
              <>
                Êtes-vous sûr de vouloir <strong>valider</strong> le compte de{' '}
                <strong>{confirmDialog.email}</strong> ?<br /><br />
                L'utilisateur recevra un email de confirmation et pourra se connecter immédiatement.
              </>
            ) : confirmDialog.action === 'reject' ? (
              <>
                Êtes-vous sûr de vouloir <strong>rejeter</strong> le compte de{' '}
                <strong>{confirmDialog.email}</strong> ?<br /><br />
                L'utilisateur ne pourra pas se connecter et devra créer un nouveau compte.
              </>
            ) : (
              <>
                Êtes-vous sûr de vouloir <strong>supprimer définitivement</strong> le compte de{' '}
                <strong>{confirmDialog.email}</strong> ?<br /><br />
                <strong style={{ color: '#d32f2f' }}>Cette action est irréversible !</strong>
                <br />
                Le compte et toutes ses données associées seront supprimés de la base de données.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            variant="contained"
            autoFocus
          >
            {confirmDialog.action === 'approve'
              ? 'Valider'
              : confirmDialog.action === 'reject'
              ? 'Rejeter'
              : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PendingUsers;
